// ============================================================================
// USE ALGORITHM RUNNER HOOK
// React hook for executing algorithms via local TypeScript generators (static mode)
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ExecutionStep,
  StepType,
  StepPayload,
} from '../types/rust-engine-interfaces';
import type { SimulationStep } from '../types/visualizer.types';
import { getStepsWithFallbacks } from '../utils/dataValidator';
import { clampExecutionSteps, clampInputDataSize } from '../utils/frameClamp';
import { availableAlgorithms, loadGenerator } from '../registry/algorithmRegistry';

// ── Phase 3: configurable local generator timeout (ms) ──────────────────────
const LOCAL_GENERATOR_TIMEOUT_MS = 10_000;

export interface UseAlgorithmRunnerState {
  isLoading: boolean;
  currentSteps: ExecutionStep[];
  executionError: Error | null;
  totalSteps: number;
  executionTimeMs: number;
  isLocalMode: boolean;
  algorithmId: string | null;
}

export interface UseAlgorithmRunnerReturn extends UseAlgorithmRunnerState {
  execute: (algorithmId: string, inputData: unknown, localGenerator?: () => ExecutionStep[]) => Promise<void>;
  reset: () => void;
}

function unwrapTaggedInput(inputData: unknown): unknown {
  if (inputData && typeof inputData === 'object' && 'data' in inputData) {
    return (inputData as { data: unknown }).data;
  }

  return inputData;
}

async function createFallbackExecutionSteps(
  algorithmId: string,
  inputData: unknown,
  reason?: unknown
): Promise<ExecutionStep[]> {
  const algoInfo = availableAlgorithms.find(a => a.id === algorithmId);
  const category = algoInfo?.category ?? 'generic';

  try {
    const { executeUniversalFallback } = await import('../fallbacks/executeUniversalFallback');
    const fallbackSteps = await executeUniversalFallback(algorithmId, inputData, category);
    if (fallbackSteps.length > 0) {
      return fallbackSteps;
    }
  } catch (fallbackError) {
    console.warn('[createFallbackExecutionSteps] Universal fallback failed:', fallbackError);
  }

  const reasonText = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';
  const name = algoInfo?.name ?? algorithmId;
  const fallbackArr = [1, 2, 3, 4, 5, 6, 7];
  return [{
    stepId: 0,
    stepType: StepType.Initialization,
    timestamp: Date.now(),
    payload: {
      type: 'array',
      items: fallbackArr.map((v, i) => ({ index: i, value: v, highlighted: false, compared: false, swapped: false, sorted: false })),
      metadata: { highlightIndices: [] },
    } as StepPayload,
    metadata: {
      explanation: `${name}: fallback visualization. ${reasonText || 'Select an algorithm to begin.'}`,
    },
  }];
}

/**
 * Run a local simulation using the TS generator files directly.
 * All generators emit ExecutionStep[] via gen-utils helpers.
 */
async function runLocalSimulation(algorithmId: string, inputData: unknown): Promise<ExecutionStep[]> {
  const algoInfo = availableAlgorithms.find(a => a.id === algorithmId);
  if (!algoInfo) {
    throw new Error(`Algorithm ${algorithmId} not found in registry`);
  }

  const generator = await loadGenerator(algoInfo.path);
  if (!generator) {
    throw new Error(`Local generator for algorithm path ${algoInfo.path} could not be loaded`);
  }

  const primaryInput = unwrapTaggedInput(inputData);

  // ── Phase 3 Timeout Guard ─────────────────────────────────────────────────
  // Wrap the synchronous generator.generate() call in a Promise.race so that
  // an algorithm with an infinite loop or massive input cannot freeze the UI
  // thread indefinitely. After LOCAL_GENERATOR_TIMEOUT_MS the race rejects.
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutGuard = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(
      () => reject(new Error(`[TimeoutGuard] Generator '${algorithmId}' exceeded ${LOCAL_GENERATOR_TIMEOUT_MS}ms limit and was aborted.`)),
      LOCAL_GENERATOR_TIMEOUT_MS
    );
  });

  const generatorRun = (async () => {
    const errors: string[] = [];
    try {
      // Candidate 1: unwrapped input (standard path)
      try {
        const simSteps = generator.generate(primaryInput);
        if (Array.isArray(simSteps) && simSteps.length > 0) {
          const first = simSteps[0] as SimulationStep | ExecutionStep;
          if (first && 'stepType' in first && 'payload' in first) {
            return simSteps as unknown as ExecutionStep[];
          }
          throw new Error(`Generator returned legacy SimulationStep[] format (unsupported)`);
        }
        throw new Error('Generator returned empty steps');
      } catch (candidateError) {
        const msg = candidateError instanceof Error ? candidateError.message : String(candidateError);
        errors.push(`unwrapped input: ${msg}`);
        console.warn(`[runLocalSimulation] ${algorithmId} unwrapped input failed:`, msg);
      }

      // Candidate 2: raw input as-is (for generators that handle tagged envelopes)
      try {
        const simSteps = generator.generate(inputData);
        if (Array.isArray(simSteps) && simSteps.length > 0) {
          const first = simSteps[0] as SimulationStep | ExecutionStep;
          if (first && 'stepType' in first && 'payload' in first) {
            console.info(`[runLocalSimulation] ${algorithmId} succeeded with raw input (candidate 2)`);
            return simSteps as unknown as ExecutionStep[];
          }
        }
        throw new Error('Generator returned empty or legacy steps');
      } catch (candidateError) {
        const msg = candidateError instanceof Error ? candidateError.message : String(candidateError);
        errors.push(`raw input: ${msg}`);
        console.warn(`[runLocalSimulation] ${algorithmId} raw input failed:`, msg);
      }

      return await createFallbackExecutionSteps(algorithmId, inputData, errors.join(' | '));
    } catch (err) {
      throw err;
    }
  })();

  return Promise.race([generatorRun, timeoutGuard]).finally(() => {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  });
}

export const useAlgorithmRunner = (): UseAlgorithmRunnerReturn => {
  const [state, setState] = useState<UseAlgorithmRunnerState>({
    isLoading: false,
    currentSteps: [],
    executionError: null,
    totalSteps: 0,
    executionTimeMs: 0,
    isLocalMode: true,
    algorithmId: null,
  });

  // ── Always-current mirror so callbacks never close over stale state ─────────
  const stateRef = useRef<UseAlgorithmRunnerState>(state);
  useEffect(() => {
    stateRef.current = state;
  });

  const executionIdRef = useRef<number>(0);

  // ── Batch incoming generator steps to avoid O(n) React renders ──────
  const stepBufferRef = useRef<ExecutionStep[]>([]);
  const flushStepBuffer = useCallback(() => {
    const buffered = stepBufferRef.current;
    if (buffered.length === 0) return;
    const nextSteps = buffered.splice(0, buffered.length);
    setState((prev: UseAlgorithmRunnerState) => ({
      ...prev,
      currentSteps: [...prev.currentSteps, ...nextSteps],
    }));
  }, []);
  useEffect(() => {
    const interval = setInterval(flushStepBuffer, 50);
    return () => {
      clearInterval(interval);
      flushStepBuffer();
    };
  }, [flushStepBuffer]);

  // Execute algorithm via local TypeScript generators (static mode)
  const execute = useCallback(async (algorithmId: string, inputData: unknown, localGenerator?: () => ExecutionStep[]) => {
    // Increment execution ID to cancel any pending executions
    executionIdRef.current += 1;
    const currentRunId = executionIdRef.current;

    // Resolve algorithm category once here so it can be forwarded to
    // clampExecutionSteps. This ensures the correct tier (TIGHT vs EXPANDED)
    // is used for frame budgeting.
    const algoCategory = availableAlgorithms.find(a => a.id === algorithmId)?.category ?? null;

    setState((prev: UseAlgorithmRunnerState) => ({
      ...prev,
      isLoading: true,
      executionError: null,
      algorithmId,
    }));

    try {
      const safeInputData = clampInputDataSize(inputData);
      let executionSteps: ExecutionStep[] = [];
      if (localGenerator) {
        executionSteps = localGenerator();
      } else {
        executionSteps = await runLocalSimulation(algorithmId, safeInputData);
      }

      const validatedSteps = getStepsWithFallbacks(executionSteps);
      const clampedSteps = validatedSteps.length > 0 ? clampExecutionSteps(validatedSteps, algorithmId, algoCategory) : validatedSteps;
      const nextSteps = clampedSteps.length > 0
        ? clampedSteps
        : await createFallbackExecutionSteps(algorithmId, inputData, 'Validator removed all local steps');
      if (currentRunId !== executionIdRef.current) return; // drop stale result
      setState((prev: UseAlgorithmRunnerState) => ({
        ...prev,
        isLoading: false,
        currentSteps: nextSteps,
        executionError: null,
      }));
    } catch (error) {
      console.error('[Local Execution] Error:', error);
      const fallbackSteps = await createFallbackExecutionSteps(algorithmId, inputData, error);
      const clampedFallbackSteps = clampExecutionSteps(fallbackSteps, algorithmId, algoCategory);
      if (currentRunId !== executionIdRef.current) return;
      setState((prev: UseAlgorithmRunnerState) => ({
        ...prev,
        isLoading: false,
        currentSteps: clampedFallbackSteps,
        executionError: null,
      }));
    }

  // stateRef is always live — no state slice deps needed here
  }, []);

  // Reset state — reads live flags from stateRef to avoid stale closure
  const reset = useCallback(() => {
    stepBufferRef.current = [];
    const snap = stateRef.current;
    setState({
      isLoading: false,
      currentSteps: [],
      executionError: null,
      totalSteps: 0,
      executionTimeMs: 0,
      isLocalMode: snap.isLocalMode,
      algorithmId: null,
    });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
};

/** Named export so external hooks (e.g. useCompareRunner) can reuse the same
 *  local-simulation path without duplicating the logic. */
export { runLocalSimulation as runLocalSimulationExport };

export const createMockExecutionRequest = (algorithmId: string = 'bubble-sort'): { algorithmId: string; inputData: unknown } => {
  return {
    algorithmId,
    inputData: {
      type: 'array',
      data: [64, 34, 25, 12, 22, 11, 90],
    },
  };
};
